import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { fetchEpicDiff } from '../../../../lib/api/epics';
import type { EpicRepoDiff } from '../../../../lib/api/epics';
import { StatusBadge } from '../../../../components/ui';

// ---------------------------------------------------------------------------
// FORGE-116 #6 — in-portal diff viewer (open by default; collapsible)
// ---------------------------------------------------------------------------

export const EpicDiffSection: React.FC<{ epicId: string }> = ({ epicId }) => {
  const [open, setOpen] = useState(true);
  const query = useQuery({
    queryKey: ['epic', 'diff', epicId],
    queryFn: () => fetchEpicDiff(epicId),
    enabled: open,
    staleTime: 60_000,
  });
  const d = query.data;
  const repoCount = d?.repos.length ?? 0;
  return (
    <section data-testid="epic-diff-section" className="bg-white border border-slate-200 rounded-xl shadow-card">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="w-full flex items-center justify-between px-4 py-3 text-left"
        aria-expanded={open}
      >
        <span className="text-sm font-semibold text-slate-900">
          {open ? '▾' : '▸'} Diff{d ? ` — ${d.fileCount} file(s), +${d.totalAdditions} −${d.totalDeletions}` : ''}
          {repoCount > 1 ? <span className="font-normal text-slate-500"> across {repoCount} repos</span> : ''}
        </span>
        <span className="text-2xs text-slate-500 font-mono">
          {d
            ? repoCount === 1
              ? `${d.repos[0].base}…${d.repos[0].head}`
              : `${repoCount} repos`
            : 'review the agents’ changes inline'}
        </span>
      </button>
      {open && (
        <div className="border-t border-slate-100 px-4 py-3 space-y-4">
          {query.isLoading && <div className="text-sm text-slate-500">Loading diff…</div>}
          {query.isError && (
            <div role="alert" className="text-sm text-danger-700">{(query.error as Error).message}</div>
          )}
          {query.isSuccess && d && d.repos.map((rd) => (
            <RepoDiffBlock key={rd.repo} rd={rd} multi={repoCount > 1} />
          ))}
        </div>
      )}
    </section>
  );
};

// FORGE-226: one block per repo an epic touched, each with its own GitHub
// compare link and file list. Cross-repo epics render several of these.
export const RepoDiffBlock: React.FC<{ rd: EpicRepoDiff; multi: boolean }> = ({ rd, multi }) => (
  <div data-testid={`repo-diff-${rd.repo}`}>
    <div className="flex items-center justify-between gap-3 mb-2">
      <div className="flex items-baseline gap-2 min-w-0">
        {multi && <span className="font-mono text-xs font-semibold text-slate-900 truncate">{rd.repo}</span>}
        {rd.merged && (
          <StatusBadge tone="success">
            merged{rd.mergedAt ? ` ${new Date(rd.mergedAt).toLocaleDateString()}` : ''}
          </StatusBadge>
        )}
        {!rd.error && (
          <span className="text-2xs tabular-nums text-slate-500 whitespace-nowrap">
            {rd.fileCount} file(s){' '}
            <span className="text-success-700">+{rd.totalAdditions}</span>{' '}
            <span className="text-danger-700">−{rd.totalDeletions}</span>
          </span>
        )}
      </div>
      <a
        href={rd.compareUrl}
        target="_blank"
        rel="noreferrer"
        className="text-2xs font-mono text-primary-600 hover:text-primary-700 hover:underline whitespace-nowrap shrink-0"
        title="Open this comparison on GitHub"
      >
        {rd.base}…{rd.head} ↗
      </a>
    </div>
    {rd.source && rd.source !== 'branch' && !rd.error && (
      <p data-testid="diff-source-note" className="mb-2 text-2xs text-slate-500">
        {rd.source === 'pr'
          ? `Rebuilt from the pull request${rd.merged ? ' — the epic branch is merged or deleted, but GitHub keeps the PR’s commits' : ''}.`
          : 'Rebuilt from the final commit Forge recorded — the epic branch no longer exists.'}
        {rd.prUrl && (
          <>{' '}<a href={rd.prUrl} target="_blank" rel="noreferrer" className="text-primary-600 hover:underline">View PR ↗</a></>
        )}
      </p>
    )}
    {rd.error ? (
      <div className="text-xs text-slate-500 italic">
        {rd.error} —{' '}
        <a href={rd.compareUrl} target="_blank" rel="noreferrer" className="not-italic text-primary-600 hover:underline">
          open on GitHub ↗
        </a>
      </div>
    ) : rd.files.length === 0 ? (
      <div className="text-xs text-slate-500 italic">No changes between {rd.base} and {rd.head}.</div>
    ) : (
      rd.files.map((f) => (
        <details key={f.filename} className="mb-2 border border-slate-100 rounded-lg">
          <summary className="cursor-pointer px-3 py-2 text-xs font-mono flex items-center justify-between">
            <span className="text-slate-800">{f.filename}</span>
            <span className="tabular-nums text-2xs">
              <span className="text-success-700">+{f.additions}</span>{' '}
              <span className="text-danger-700">−{f.deletions}</span>{' '}
              <StatusBadge tone={f.status === 'added' ? 'success' : f.status === 'removed' ? 'danger' : 'info'}>{f.status}</StatusBadge>
            </span>
          </summary>
          {f.patch ? (
            <pre className="text-2xs overflow-x-auto px-3 py-2 bg-slate-900 text-slate-100 rounded-b-lg whitespace-pre">
              {f.patch}{f.truncated ? '\n… (truncated — open in GitHub for the full diff)' : ''}
            </pre>
          ) : (
            <div className="px-3 py-2 text-2xs text-slate-500 italic">No inline patch (binary or too large).</div>
          )}
        </details>
      ))
    )}
  </div>
);
